import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import styles from './EventSubmissionForm.module.scss';
import Button from '../Button';
import {
    GoogleReCaptchaProvider,
    GoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { Form, Formik } from 'formik';
import InputGroup from '../InputGroup';
import inputGroupStyles from '../InputGroup/InputGroup.module.scss';
import * as Yup from 'yup';
import { uploadImageApi, eventsubmissionApi } from '../../utils/api';

import { keysToSnakeFromCamel, camelToSnake } from '../../utils/caseconverters';
import i18n from '../../i18n';
import Modal from '../Modal';
import Icon from '../Icon';
import { Error } from '../InputGroup/InputGroup';
import Loader from '../Loader';
import { CsrfContext } from '../../containers/ArticlePage/ArticlePage';
import { RecaptchaDisclaimer } from '../EventSubmissionFormV2/EventSubmissionFormV2';

const isTime = (value) => /^([0-1][0-9]|[2][0-3]):([0-5][0-9])$/gm.test(value);

const EventSubmissionForm = ({
    categories = [],
    successText = '',
    failureText,
    consentText = '',
}) => {
    const [showSingleDate, setShowSingleDate] = useState(true);
    const [showCurrentErrors, setShowCurrentErrors] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showFailure, setShowFailure] = useState(false);
    const [imageValidationMessage, setImageValidationMessage] = useState('');
    const [dateValidationError, setDateValidationError] = useState({});
    const [token, setToken] = useState('');
    const csrf = useContext(CsrfContext);
    const imageRef = React.useRef();

    // Open the privacy policy in new window, so we don't lose state when pressing back button
    // also add an icon to indicate that the link will open in new tab
    consentText = consentText.replace(
        ' href',
        ' class=' +
            styles['EventSubmissionForm__ConsentLink'] +
            ' target="_blank" rel="noopener noreferrer" href'
    );

    const recaptchaCallback = (callbackToken) => {
        if (!!token) {
            return;
        }
        setToken(callbackToken);
    };

    const handleSubmit = async (values, resetForm) => {
        try {
            let imageFile = '';
            let imageTitle = '';
            let imageCredit = '';

            if (!!values.image) {
                imageFile = values.file;
                imageTitle = values.file.name;
                imageCredit = values.photoCredit;
            }

            let formData = new FormData();

            // Image to be uploaded
            formData.append('imageFile', imageFile);
            formData.append('imageTitle', imageTitle);
            formData.append('imageCredit', imageCredit);

            // Event data
            formData.append('titleEn', values.titleEn);
            formData.append('titleSv', values.titleSv);
            formData.append('descriptionEn', values.descriptionEn);
            formData.append('descriptionSv', values.descriptionSv);
            formData.append('externalWebsiteUrl', values.externalWebsiteUrl);
            formData.append('address', values.address);
            formData.append('venueName', values.venueName);
            formData.append('zipCode', values.zip);
            formData.append('submittedByEmail', values.submittedByEmail);
            formData.append('city', values.city);
            formData.append(
                'categories',
                JSON.stringify(values.categories.map(Number))
            );
            formData.append(
                'dateAdmin',
                JSON.stringify(
                    keysToSnakeFromCamel(values.dateAdmin).map((x) => ({
                        ...x,
                        type: camelToSnake(x.type),
                    }))
                )
            );

            formData.append('recaptchaToken', token);

            const response = await fetch('/api/v1/eventsubmission/', {
                method: 'post',
                headers: {
                    Accept: 'application/json',
                    'X-CSRFToken': csrf,
                },
                body: formData,
            });

            if (response.status !== 201) {
                const errorMessage = await response.text();
                throw `Failed to create resource. Status: ${response.status}, Message: ${errorMessage}`;
            }

            resetForm({});
            setShowSuccess(true);
        } catch (error) {
            console.error(error);
            setShowFailure(true);
        }
    };

    const handleAddDateRange = (values, setFieldValue) => {
        const { dateAdmin, startDate, endDate } = values;
        const schema = Yup.object().shape({
            startDate: Yup.date().typeError(
                i18n.t('EventSubmissionForm.validation.date')
            ),
            endDate: Yup.date()
                .typeError(i18n.t('EventSubmissionForm.validation.date'))
                .min(Yup.ref('startDate')),
        });

        schema
            .validate({
                endDate,
                startDate,
            })
            .then(function () {
                setFieldValue('dateAdmin', [
                    ...dateAdmin,
                    dateRangeObject(startDate, endDate),
                ]);
                setFieldValue('startDate', '');
                setFieldValue('endDate', '');
                setDateValidationError({});
            })
            .catch(function (err) {
                const { path, message } = err;
                setDateValidationError({
                    [path]: message,
                });
            });
    };

    const handleAddSingleDate = (values, setFieldValue) => {
        const { dateAdmin, date, startTime, endTime } = values;
        let schema = Yup.object().shape({
            date: Yup.date().typeError(
                i18n.t('EventSubmissionForm.validation.date')
            ),
            startTime: Yup.string()
                .required(i18n.t('EventSubmissionForm.validation.required'))
                .test(
                    'is-time-format',
                    i18n.t('EventSubmissionForm.validation.time'),
                    isTime
                ),
            endTime: Yup.string()
                .required(i18n.t('EventSubmissionForm.validation.required'))
                .test(
                    'is-time-format',
                    i18n.t('EventSubmissionForm.validation.time'),
                    isTime
                ),
        });

        schema
            .validate({
                date,
                startTime,
                endTime,
            })
            .then(function () {
                setFieldValue('dateAdmin', [
                    ...dateAdmin,
                    singleDateObject(date, startTime, endTime),
                ]);
                setFieldValue('date', '');
                setFieldValue('startTime', '');
                setFieldValue('endTime', '');
                setDateValidationError({});
            })
            .catch(function (err) {
                const { path, message } = err;
                setDateValidationError({
                    [path]: message,
                });
            });
    };

    const handleRemoveDateAdminItem = (id, values, setFieldValue) => {
        const { dateAdmin } = values;
        setFieldValue(
            'dateAdmin',
            dateAdmin.filter((item) => item.id !== id)
        );
    };

    const handleImageSelected = (e, setFieldValue) => {
        const file = e.currentTarget.files[0];
        const size = (file.size / (1024 * 1024)).toFixed(2);
        if (size > 10) {
            setImageValidationMessage(
                i18n.t('EventSubmissionForm.validation.tooLargeImage')
            );
            setFieldValue('file', '');
            setFieldValue('image', '');
        } else {
            setImageValidationMessage('');
            setFieldValue('file', file);
            setFieldValue('image', e.target.value);
        }
    };

    return (
        <div className={styles['EventSubmissionForm']}>
            {!!showSuccess && (
                <Dialog
                    onClose={() => setShowSuccess(false)}
                    title={i18n.t('EventSubmissionForm.successTitle')}
                    text={successText}
                />
            )}
            {!!showFailure && (
                <Dialog
                    onClose={() => setShowFailure(false)}
                    title={i18n.t('EventSubmissionForm.failureTitle')}
                    text={failureText}
                />
            )}
            <GoogleReCaptchaProvider
                reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
                <Formik
                    initialValues={{
                        titleEn: '',
                        titleSv: '',
                        descriptionEn: '',
                        descriptionSv: '',
                        externalWebsiteUrl: '',
                        venueName: '',
                        address: '',
                        zip: '',
                        city: '',
                        image: '',
                        photoCredit: '',
                        startDate: '',
                        endDate: '',
                        date: '',
                        startTime: '',
                        endTime: '',
                        location: {},
                        categories: [],
                        dateAdmin: [],
                        submittedByEmail: '',
                        consent: [],
                    }}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                        handleSubmit(values, resetForm);
                        setSubmitting(false);
                    }}
                    validationSchema={ErrorMessageSchema}>
                    {({
                        isSubmitting,
                        setFieldValue,
                        values,
                        isValid,
                        errors,
                    }) => (
                        <Form>
                            <p className={styles['EventSubmissionForm__Text']}>
                                {i18n.t(
                                    'EventSubmissionForm.requiredFieldsInfo'
                                )}
                            </p>
                            <fieldset
                                className={
                                    styles['EventSubmissionForm__Section']
                                }>
                                <legend
                                    className={
                                        styles[
                                            'EventSubmissionForm__SectionHeader'
                                        ] +
                                        ' ' +
                                        styles[
                                            'EventSubmissionForm__SectionHeader--One'
                                        ]
                                    }>
                                    {i18n.t(
                                        'EventSubmissionForm.sectionHeaderName'
                                    )}
                                </legend>
                                <InputGroup
                                    name="titleEn"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.titleEn'
                                    )}
                                    type="text"
                                    placeholder=""
                                    infoMessage=""
                                />
                                <InputGroup
                                    name="titleSv"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.titleSv'
                                    )}
                                    type="text"
                                    placeholder=""
                                    infoMessage=""
                                />
                                <InputGroup
                                    name="address"
                                    autoComplete="off"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.address'
                                    )}
                                    infoMessage=""
                                />
                                <InputGroup
                                    name="zip"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.zipCode'
                                    )}
                                />
                                <InputGroup
                                    name="city"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.city'
                                    )}
                                />
                                <InputGroup
                                    name="venueName"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.venueName'
                                    )}
                                    placeholder={i18n.t(
                                        'EventSubmissionForm.placeholders.venueName'
                                    )}
                                    infoMessage=""
                                />
                                <InputGroup
                                    name="externalWebsiteUrl"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.websiteUrl'
                                    )}
                                    type="text"
                                    infoMessage=""
                                />
                            </fieldset>
                            <fieldset
                                className={
                                    styles['EventSubmissionForm__Section']
                                }>
                                <legend
                                    className={
                                        styles[
                                            'EventSubmissionForm__SectionHeader'
                                        ] +
                                        ' ' +
                                        styles[
                                            'EventSubmissionForm__SectionHeader--Two'
                                        ]
                                    }>
                                    {i18n.t(
                                        'EventSubmissionForm.sectionHeaderDates'
                                    )}
                                </legend>

                                <RadioDateInputType
                                    onChange={setShowSingleDate}
                                />

                                {!showSingleDate && (
                                    <div
                                        className={
                                            styles[
                                                'EventSubmissionForm__DateContainer'
                                            ]
                                        }>
                                        <InputGroup
                                            placeholder="Start"
                                            type="date"
                                            name="startDate"
                                            id="startDate"
                                            label={i18n.t(
                                                'EventSubmissionForm.labels.startDate'
                                            )}
                                            modifiers={[
                                                inputGroupStyles[
                                                    'InputGroup--Inline'
                                                ],
                                            ]}
                                            extraErrorMessage={
                                                dateValidationError?.startDate
                                            }
                                        />

                                        <InputGroup
                                            type="date"
                                            name="endDate"
                                            id="endDate"
                                            placeholder="End"
                                            label={i18n.t(
                                                'EventSubmissionForm.labels.endDate'
                                            )}
                                            modifiers={[
                                                inputGroupStyles[
                                                    'InputGroup--Inline'
                                                ],
                                            ]}
                                            extraErrorMessage={
                                                dateValidationError?.endDate
                                            }
                                        />
                                        <Button
                                            type="secondary"
                                            text={i18n.t('generic.add')}
                                            onClick={(e) => {
                                                handleAddDateRange(
                                                    values,
                                                    setFieldValue
                                                );
                                                e.preventDefault();
                                            }}
                                        />
                                    </div>
                                )}
                                {!!showSingleDate && (
                                    <div
                                        className={
                                            styles[
                                                'EventSubmissionForm__DateContainer'
                                            ]
                                        }>
                                        <InputGroup
                                            placeholder="Date"
                                            type="date"
                                            name="date"
                                            id="date"
                                            label={i18n.t(
                                                'EventSubmissionForm.labels.date'
                                            )}
                                            modifiers={[
                                                inputGroupStyles[
                                                    'InputGroup--Inline'
                                                ],
                                            ]}
                                            extraErrorMessage={
                                                dateValidationError?.date
                                            }
                                        />
                                        <InputGroup
                                            type="time"
                                            name="startTime"
                                            id="startTime"
                                            placeholder="Start time"
                                            label={i18n.t(
                                                'EventSubmissionForm.labels.startTime'
                                            )}
                                            modifiers={[
                                                inputGroupStyles[
                                                    'InputGroup--Inline'
                                                ],
                                            ]}
                                            extraErrorMessage={
                                                dateValidationError?.startTime
                                            }
                                        />
                                        <InputGroup
                                            type="time"
                                            name="endTime"
                                            id="endTime"
                                            placeholder="End time"
                                            label={i18n.t(
                                                'EventSubmissionForm.labels.endTime'
                                            )}
                                            modifiers={[
                                                inputGroupStyles[
                                                    'InputGroup--Inline'
                                                ],
                                            ]}
                                            extraErrorMessage={
                                                dateValidationError?.endTime
                                            }
                                        />
                                        <Button
                                            type="secondary"
                                            text="Add"
                                            onClick={(e) => {
                                                handleAddSingleDate(
                                                    values,
                                                    setFieldValue
                                                );
                                                e.preventDefault();
                                            }}>
                                            Add
                                        </Button>
                                    </div>
                                )}
                                <InputGroup name="dateAdmin" type="hidden" />
                                {!!values.dateAdmin.length && (
                                    <DateAdminValueList
                                        onRemove={handleRemoveDateAdminItem}
                                        setFieldValue={setFieldValue}
                                        values={values}
                                    />
                                )}
                            </fieldset>
                            <fieldset
                                className={
                                    styles['EventSubmissionForm__Section']
                                }>
                                <legend
                                    className={
                                        styles[
                                            'EventSubmissionForm__SectionHeader'
                                        ] +
                                        ' ' +
                                        styles[
                                            'EventSubmissionForm__SectionHeader--Three'
                                        ]
                                    }>
                                    {i18n.t(
                                        'EventSubmissionForm.sectionHeaderMore'
                                    )}
                                </legend>

                                <InputGroup
                                    name="descriptionEn"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.descriptionEn'
                                    )}
                                    type="textarea"
                                />
                                <InputGroup
                                    name="descriptionSv"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.descriptionSv'
                                    )}
                                    type="textarea"
                                />
                            </fieldset>
                            <fieldset
                                className={
                                    styles['EventSubmissionForm__Section']
                                }>
                                <legend
                                    className={
                                        styles[
                                            'EventSubmissionForm__SectionHeader'
                                        ] +
                                        ' ' +
                                        styles[
                                            'EventSubmissionForm__SectionHeader--Four'
                                        ]
                                    }>
                                    {i18n.t(
                                        'EventSubmissionForm.sectionHeaderImageUpload'
                                    )}
                                </legend>

                                <div
                                    className={
                                        styles['EventSubmissionForm__ImageInfo']
                                    }>
                                    {i18n.t('EventSubmissionForm.imageInfo')}
                                </div>

                                <InputGroup
                                    name="image"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.chooseImage'
                                    )}
                                    type="file"
                                    innerRef={imageRef}
                                    validate={() => {
                                        if (
                                            imageRef.current?.files[0]?.name
                                                ?.length > 50
                                        ) {
                                            return i18n.t(
                                                'EventSubmissionForm.validation.filenameLength'
                                            );
                                        }
                                    }}
                                    infoMessage={i18n.t(
                                        'EventSubmissionForm.chooseImageInfo'
                                    )}
                                    accept="image/*"
                                    onChange={(e) =>
                                        handleImageSelected(e, setFieldValue)
                                    }
                                    extraErrorMessage={imageValidationMessage}
                                />
                                <InputGroup
                                    name="photoCredit"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.photoCredit'
                                    )}
                                    type="text"
                                />
                                <InputGroup
                                    name="photoUploadAllowed"
                                    type="checkbox"
                                    i18nlabel="EventSubmissionForm.labels.photoUploadAllowed"
                                    items={[
                                        {
                                            value: 'photoUploadAllowed',
                                            label: i18n.t(
                                                'EventSubmissionForm.labels.photoUploadAllowed'
                                            ),
                                        },
                                    ]}
                                />
                            </fieldset>
                            <fieldset
                                className={
                                    styles['EventSubmissionForm__Section']
                                }>
                                <legend
                                    className={
                                        styles[
                                            'EventSubmissionForm__SectionHeader'
                                        ] +
                                        ' ' +
                                        styles[
                                            'EventSubmissionForm__SectionHeader--Five'
                                        ]
                                    }>
                                    {i18n.t(
                                        'EventSubmissionForm.sectionHeaderCategories'
                                    )}
                                </legend>

                                <InputGroup
                                    name="categories"
                                    type="checkbox"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.categories'
                                    )}
                                    items={categories.map((x) => ({
                                        ...x,
                                        name: 'categories',
                                    }))}
                                />
                            </fieldset>
                            <fieldset
                                className={
                                    styles['EventSubmissionForm__Section']
                                }>
                                <legend
                                    className={
                                        styles[
                                            'EventSubmissionForm__SectionHeader'
                                        ] +
                                        ' ' +
                                        styles[
                                            'EventSubmissionForm__SectionHeader--Six'
                                        ]
                                    }>
                                    {i18n.t(
                                        'EventSubmissionForm.sectionHeaderSubmittedBy'
                                    )}
                                </legend>
                                <InputGroup
                                    name="submittedByEmail"
                                    label={i18n.t(
                                        'EventSubmissionForm.labels.submittedByEmail'
                                    )}
                                    type="email"
                                    autoComplete="email"
                                />
                                <InputGroup
                                    name="consent"
                                    type="checkbox"
                                    items={[
                                        {
                                            value: 'consent',
                                            label:
                                                consentText ||
                                                i18n.t(
                                                    'EventSubmissionForm.labels.consent'
                                                ),
                                        },
                                    ]}
                                />
                            </fieldset>

                            {showCurrentErrors && (
                                <div
                                    className={
                                        styles['EventSubmissionForm__Errors']
                                    }>
                                    <div
                                        className={
                                            styles[
                                                'EventSubmissionForm__ErrorsListInfo'
                                            ]
                                        }>
                                        {i18n.t(
                                            'EventSubmissionForm.errorListInfo'
                                        )}
                                    </div>
                                    <ul
                                        className={
                                            styles[
                                                'EventSubmissionForm__ErrorsList'
                                            ]
                                        }>
                                        {Object.values(errors).map((error) => (
                                            <li
                                                className={
                                                    styles[
                                                        'EventSubmissionForm__ErrorsListItem'
                                                    ]
                                                }>
                                                {error}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <Button
                                submit
                                text={i18n.t(
                                    'EventSubmissionForm.labels.submit'
                                )}
                                onClick={() => {
                                    if (!isValid) setShowCurrentErrors(true);
                                }}
                                disabled={isSubmitting}
                                type="primary"
                            />
                            {isSubmitting && <Loader position="left" />}
                            <RecaptchaDisclaimer />
                        </Form>
                    )}
                </Formik>
                <GoogleReCaptcha onVerify={recaptchaCallback} />
            </GoogleReCaptchaProvider>
        </div>
    );
};

EventSubmissionForm.propTypes = {
    categories: PropTypes.array,
    successText: PropTypes.string,
    consentText: PropTypes.string,
};

const ErrorMessageSchema = Yup.object().shape({
    titleEn: Yup.string().required(
        i18n.t('EventSubmissionForm.validation.titleEnRequired')
    ),
    titleSv: Yup.string().required(
        i18n.t('EventSubmissionForm.validation.titleSvRequired')
    ),
    descriptionEn: Yup.string()
        .min(
            100,
            i18n.t('EventSubmissionForm.validation.minLength', { length: 100 })
        )
        .max(
            500,
            i18n.t('EventSubmissionForm.validation.maxLength', { length: 500 })
        )
        .required(
            i18n.t('EventSubmissionForm.validation.descriptionEnRequired')
        ),
    descriptionSv: Yup.string()
        .min(
            100,
            i18n.t('EventSubmissionForm.validation.minLength', { length: 100 })
        )
        .max(
            500,
            i18n.t('EventSubmissionForm.validation.maxLength', { length: 500 })
        )
        .required(
            i18n.t('EventSubmissionForm.validation.descriptionSvRequired')
        ),
    externalWebsiteUrl: Yup.string().url(
        i18n.t('EventSubmissionForm.validation.url')
    ),
    image: Yup.mixed(),
    address: Yup.string().required(
        i18n.t('EventSubmissionForm.validation.addressRequired')
    ),
    city: Yup.string().required(
        i18n.t('EventSubmissionForm.validation.cityRequired')
    ),
    zip: Yup.string().required(
        i18n.t('EventSubmissionForm.validation.zipRequired')
    ),
    categories: Yup.array().required(
        i18n.t('EventSubmissionForm.validation.atLeastOneCategory')
    ),
    dateAdmin: Yup.array()
        .min(1, i18n.t('EventSubmissionForm.validation.atLeastOneOccurrence'))
        .required(
            i18n.t('EventSubmissionForm.validation.atLeastOneOccurrence')
        ),
    submittedByEmail: Yup.string()
        .email(i18n.t('EventSubmissionForm.validation.email'))
        .required(
            i18n.t('EventSubmissionForm.validation.submittedByEmailRequired')
        ),
    consent: Yup.array().test({
        name: 'consent_test',
        exclusive: true,
        message: i18n.t('EventSubmissionForm.validation.youMustAgree'),
        test: (value) => value.length > 0,
    }),
    // image: Yup.mixed().test({
    //     name: 'filename_length_test',
    //     exclusive: true,
    //     message: i18n.t('EventSubmissionForm.validation.fileNameTooLong'),
    //     test: (value = {}) => console.log(typeof value)
    // }),
    photoUploadAllowed: Yup.array().when('image', {
        is: (x) => !!x,
        then: Yup.array()
            .test({
                name: 'accept_upload_test',
                exclusive: true,
                message: i18n.t(
                    'EventSubmissionForm.validation.youMustAgreeUpload'
                ),
                test: (value = []) => value.length > 0,
            })
            .required(),
        else: Yup.array(),
    }),
});

// https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid/2117523#2117523
const uuidv4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
            var r = (Math.random() * 16) | 0,
                v = c == 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        }
    );
};

const singleDateObject = (date, startTime, endTime) => ({
    id: uuidv4(),
    type: 'single_date',
    value: {
        date,
        startTime,
        endTime,
    },
});
const dateRangeObject = (startDate, endDate) => ({
    id: uuidv4(),
    type: 'date_range',
    value: { startDate, endDate },
});

const DateAdminValueList = ({ values, onRemove, setFieldValue }) => (
    <ul className={styles['EventSubmissionForm__DateValues']}>
        {values.dateAdmin.map(({ value, id }, i) => {
            return (
                <li
                    key={i}
                    className={styles['EventSubmissionForm__DateValue']}>
                    {!!value.startDate && (
                        <span
                            className={
                                styles['EventSubmissionForm__DateValueText']
                            }>
                            {value.startDate} {value.endDate}
                        </span>
                    )}
                    {!!value.date && (
                        <span>
                            {value.date} {value.startTime} — {value.endTime}
                        </span>
                    )}
                    <button
                        type="button"
                        className={
                            styles['EventSubmissionForm__DateValueRemove']
                        }
                        aria-label={i18n.t(
                            'EventSubmissionForm.removeDateValue'
                        )}
                        onClick={(e) => {
                            onRemove(id, values, setFieldValue);
                            e.preventDefault();
                        }}>
                        X
                    </button>
                </li>
            );
        })}
    </ul>
);
DateAdminValueList.propTypes = {
    values: PropTypes.object,
    onRemove: PropTypes.func,
    setFieldValue: PropTypes.func,
};

export const RadioDateInputType = ({ onChange }) => (
    <ul className={styles['EventSubmissionForm__DateType']}>
        <li className={styles['EventSubmissionForm__Radio']}>
            <input
                name="dateInputType"
                value="single"
                id="single"
                type="radio"
                onChange={(e) => onChange(true)}
                defaultChecked
            />
            <span className={styles['EventSubmissionForm__RadioMark']} />
            <label
                htmlFor="single"
                className={styles['EventSubmissionForm__DateTypeLabel']}>
                {i18n.t('EventSubmissionForm.labels.singleDays')}
            </label>
        </li>
        <li className={styles['EventSubmissionForm__Radio']}>
            <input
                name="dateInputType"
                value="range"
                id="range"
                type="radio"
                onChange={(e) => onChange(false)}
            />
            <span className={styles['EventSubmissionForm__RadioMark']} />
            <label
                htmlFor="range"
                className={styles['EventSubmissionForm__DateTypeLabel']}>
                {i18n.t('EventSubmissionForm.labels.dateRange')}
            </label>
        </li>
    </ul>
);

const Dialog = ({ onClose, title, text }) => (
    <Modal>
        <div className={styles['EventSubmissionForm__Dialog']}>
            <button
                aria-label={i18n.t('Modal.close')}
                label={i18n.t('Modal.close')}
                className={styles['EventSubmissionForm__DialogCloseButton']}
                onClick={onClose}>
                <Icon type="close" color="black800" size="large" />
            </button>
            <strong className={styles['EventSubmissionForm__DialogTitle']}>
                {title}
            </strong>
            <p className={styles['EventSubmissionForm__DialogText']}>{text}</p>
        </div>
    </Modal>
);
export default EventSubmissionForm;
